/**
 * PremiumPackagesPage
 *
 * Displays all available premium packages with subscription status.
 * Matches Flutter: lib/presentation/screens/premium/premium_packages_screen.dart
 *
 * The bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`). Flutter renders
 * `PremiumPackageAppBar` here (`premium_packages_screen.dart:120-124`), which is
 * a byte-identical copy of the shared `BaseAppBar`
 * (`premium_package_app_bar.dart:5-38` vs `base_app_bar.dart:5-42`: back-chevron
 * leading → `onBackPress`, `centerTitle: true`, `backgroundColor`,
 * `preferredSize = kToolbarHeight`) — i.e. Flutter's own evidence that this is
 * the shared page-bar contract, of which the web now has exactly one
 * implementation. No `leadingQa` is passed on purpose: this page never set one,
 * so the back control keeps `BackIconButton`'s default `back-button` id.
 *
 * Back is the deep-link-guarded `Navigator.goBackOr` (`presentation.md` → App bar
 * → "Back affordance"), a strict superset of Flutter's plain `_onBackPress` →
 * `NavigationService.pop()` (`premium_packages_screen.dart:55-58`): identical
 * whenever there is in-app history, and only different on the entry Flutter
 * cannot have at all — a bookmarked/pasted `/premium-packages` URL.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Architecture: Component → Hook → UseCase → SecureStorage
 */
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { PremiumPackageItem } from './components/PremiumPackageItem';
import { IdentityPremiumPackageItem } from './components/IdentityPremiumPackageItem';
import { usePremiumPackages } from './hooks/usePremiumPackages';

export const PremiumPackagesPage = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const location = useLocation();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  const {
    premiumPackages,
    isLoading,
    fetchPremiumPackages,
    getPackageStatus,
    getIdentityVerificationStatus,
    isIdentityPackage,
    handleSubscribe,
    handleRestorePurchases,
    handleGetVerified,
    handleIdentityInfo,
  } = usePremiumPackages({
    showSubscribeInfo: (pkg) => InfoSheet.show({
      title: `${l10n.premiumPackages.subscribeInfoTitle} - ${pkg.name}`,
      infoText: l10n.premiumPackages.subscribeInfoMessage,
      forceDialog: true,
      maxWidth: responsive.infoDialog.maxWidth,
      screenKey,
    }),
    showRestoreInfo: () => InfoSheet.show({
      title: l10n.premiumPackages.restoreInfoTitle,
      infoText: l10n.premiumPackages.restoreInfoMessage,
      forceDialog: true,
      maxWidth: responsive.infoDialog.maxWidth,
      screenKey,
    }),
    showVerifyInfo: () => InfoSheet.show({
      title: l10n.premiumPackages.verifyInfoTitle,
      infoText: l10n.premiumPackages.verifyInfoMessage,
      forceDialog: true,
      maxWidth: responsive.infoDialog.maxWidth,
      screenKey,
    }),
    showIdentityInfo: () => InfoSheet.show({
      title: l10n.premiumPackages.identityInfoTitle,
      infoText: l10n.premiumPackages.identityInfoMessage,
      forceDialog: true,
      maxWidth: responsive.infoDialog.maxWidth,
      screenKey,
    }),
  });

  useBasePage({
    screenKey,
    onInit: () => { fetchPremiumPackages(); },
    onPush: () => { },
  });

  // Coarse lifecycle token for the page root, derived from existing state only.
  const pageStatus = isLoading
    ? 'loading'
    : premiumPackages.length === 0
      ? 'empty'
      : 'ready';

  return (
    <div
      {...qaAttr({ id: 'premium-packages', status: pageStatus })}
      style={{
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* App Bar — back leading + centered title, no `leadingQa` so the back
          control keeps `BackIconButton`'s default `back-button` id.
          Deep-link fallback is the Feed: this route has no single parent — it is
          pushed from the home top-bar menu, a feed content card, the publish
          sheet and a push notification alike — so the home shell is the only
          common ancestor of every in-app entry. */}
      <ExpauseAppBar
        rootQa={{ id: 'premium-packages-app-bar' }}
        title={l10n.premiumPackages.title}
        onLeadingTap={() => Navigator.goBackOr(() => Navigator.navigateToFeed())}
      />

      {/* Scrollable content */}
      <div
        {...qaAttr({ id: 'premium-packages-list' })}
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingBottom: responsive.spacing['2xl'],
        }}
      >
        {/* Header: policy text + restore purchases */}
        <div
          style={{
            padding: `${responsive.spacing.lg}px ${responsive.spacing.base}px ${responsive.spacing.sm}px`,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: responsive.font.sm,
              color: theme.text.secondary,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {l10n.premiumPackages.policyText}
            <span
              onClick={() => Navigator.navigateToPremiumSubscriptionsPolicy()}
              style={{
                color: theme.blue,
                fontWeight: responsive.fontWeight.semibold,
                cursor: 'pointer',
              }}
            >
              {l10n.premiumPackages.premiumSubscriptionsPolicy}
            </span>
            {l10n.premiumPackages.and}
            <span
              onClick={() => Navigator.navigateToCoinsPolicy()}
              style={{
                color: theme.blue,
                fontWeight: responsive.fontWeight.semibold,
                cursor: 'pointer',
              }}
            >
              {l10n.premiumPackages.coinsPolicy}
            </span>
          </p>

          {/* Restore Purchases button */}
          <button
            onClick={handleRestorePurchases}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginTop: responsive.spacing.base,
              padding: responsive.spacing.sm,
            }}
          >
            <span
              style={{
                fontSize: responsive.font.base,
                fontWeight: responsive.fontWeight.medium,
                color: theme.blue,
              }}
            >
              {l10n.premiumPackages.restorePurchases}
            </span>
          </button>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: responsive.spacing.xl,
            }}
          >
            <ExpauseLoadingIndicator />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && premiumPackages.length === 0 && (
          <p
            style={{
              textAlign: 'center',
              color: theme.text.secondary,
              fontSize: responsive.font.base,
              padding: responsive.spacing.xl,
            }}
          >
            {l10n.premiumPackages.noPremiumPackages}
          </p>
        )}

        {/* Package list */}
        {!isLoading &&
          premiumPackages.map((pkg) => {
            const status = getPackageStatus(pkg.productId);

            if (isIdentityPackage(pkg.productId)) {
              return (
                <IdentityPremiumPackageItem
                  key={pkg.productId}
                  package={pkg}
                  status={status}
                  verificationStatus={getIdentityVerificationStatus()}
                  onSubscribe={() => handleSubscribe(pkg)}
                  onGetVerified={handleGetVerified}
                  onIdentityInfo={handleIdentityInfo}
                />
              );
            }

            return (
              <PremiumPackageItem
                key={pkg.productId}
                package={pkg}
                status={status}
                onSubscribe={() => handleSubscribe(pkg)}
              />
            );
          })}
      </div>
    </div>
  );
};
